import { useMemo } from 'react';
import { fetchNodeHeatmap } from '../../api';
import { buildNodeParams } from './nodeFilterUtils';
import useNodeRequest from './useNodeRequest';
import { formatCalls, resolveRampClass } from './nodeChartTheme';

/**
 * Тепловая карта чек-листа: оператор × пункт.
 *
 * Оценки узлов приходят в 0..1, а и рампа темы, и подписи ячеек работают в процентах —
 * нормализуем при отрисовке. Прогнать 0.67 через рампу напрямую значило бы покрасить
 * хорошую оценку в красный, то есть получить карту, целиком залитую тревожным цветом.
 *
 * Операторы — строки, пункты — столбцы: карта живёт на вкладке «Сотрудники», и строка
 * здесь читается как профиль одного оператора. Операторов не режем по количеству: они
 * растут вниз, а вертикальный скролл страницы дешевле горизонтального скролла внутри
 * карточки, в который упирались колонки.
 */
/** Оценка ячейки в процентах: доли 0..1 читаются хуже, чем «67 %». */
const formatScore = (score) =>
  score === null || score === undefined ? '—' : `${Math.round(score * 100)}%`;

/**
 * Ключ ячейки «пункт + оператор».
 *
 * Звонки без распознанного оператора приходят в ячейках с `employeeId: null`, а строка
 * для них — с пустым Guid: другого идентификатора у синтетической строки быть не может.
 * Без приведения к общему виду ключи не совпадали, и строка «Без оператора» оставалась
 * пустой, хотя данные для неё приходили.
 */
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const normalizeEmployeeId = (employeeId) =>
  !employeeId || employeeId === EMPTY_GUID ? '' : employeeId;

const cellKey = (itemKey, employeeId) => itemKey + ' ' + normalizeEmployeeId(employeeId);

/** Среднее средних по непустым ячейкам. Ключ группировки задаёт вызывающий. */
const averageBy = (cells, keyOf) => {
  const totals = new Map();
  cells.forEach((cell) => {
    if (cell.avgScore === null || cell.avgScore === undefined) {
      return;
    }
    const key = keyOf(cell);
    const current = totals.get(key) ?? { sum: 0, count: 0 };
    totals.set(key, { sum: current.sum + cell.avgScore, count: current.count + 1 });
  });

  return new Map(
    [...totals.entries()].map(([key, value]) => [key, value.count ? value.sum / value.count : null])
  );
};

const NodeHeatmapChart = ({ chart, dateRange, callType, nodeFilter, packVersion, scope, onSelect }) => {
  const params = useMemo(
    () => buildNodeParams({ chart, dateRange, callType, nodeFilter, packVersion, scope }),
    [chart, dateRange, callType, nodeFilter, packVersion, scope]
  );

  const { data, error } = useNodeRequest({
    fetcher: fetchNodeHeatmap,
    params,
    errorText: 'Не удалось загрузить тепловую карту.',
    enabled: Boolean(params.date_from && params.date_to)
  });

  const { items, employees, cellIndex, itemAverages, employeeAverages, overallAverage } = useMemo(() => {
    const cells = data?.cells ?? [];

    const scored = cells.filter((cell) => cell.avgScore !== null && cell.avgScore !== undefined);
    const overall = scored.length
      ? scored.reduce((sum, cell) => sum + cell.avgScore, 0) / scored.length
      : null;

    return {
      items: data?.items ?? [],
      employees: data?.employees ?? [],
      cellIndex: new Map(cells.map((cell) => [cellKey(cell.itemKey, cell.employeeId), cell])),
      itemAverages: averageBy(cells, (cell) => cell.itemKey),
      employeeAverages: averageBy(cells, (cell) => normalizeEmployeeId(cell.employeeId)),
      overallAverage: overall
    };
  }, [data]);

  if (error) {
    return <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>;
  }

  if (items.length === 0 || employees.length === 0) {
    return <p className="text-sm text-muted-foreground">Нет данных за выбранный период.</p>;
  }

  return (
    <div className="overflow-x-auto">
      {/* `w-max min-w-full`, а не `w-full`: иначе браузер ужимает колонки под ширину
          карточки, и `min-w` заголовков-пунктов не работает — длинные формулировки
          сжимаются в лапшу вместо переноса по словам. */}
      <table className="w-max min-w-full border-collapse text-xs">
        <thead>
          <tr>
            {/* Залипающая колонка: без неё при горизонтальном скролле теряется,
                к какому оператору относится строка. */}
            <th className="sticky left-0 z-10 bg-card p-2 text-left font-medium">Сотрудник</th>
            {items.map((item) => (
              <th key={item.code} className="p-2 text-center align-bottom font-medium" title={item.label}>
                {/* Ширина живёт на внутреннем блоке, а не на ячейке: в таблице с авторазметкой
                    `max-width` колонки не ограничивает, и формулировки пунктов растягивали
                    шапку в одну строку на 200+ пикселей вместо переноса по словам. */}
                <span className="mx-auto block w-28 whitespace-normal break-words max-sm:w-20">{item.label}</span>
              </th>
            ))}
            <th className="p-2 text-center align-bottom font-medium">Средняя</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => {
            const average = employeeAverages.get(normalizeEmployeeId(employee.employeeId));

            return (
              <tr key={employee.employeeId}>
                <td
                  className="sticky left-0 z-10 border-t bg-card p-2 align-middle"
                  title={employee.name}
                >
                  <span className="block whitespace-normal break-words max-sm:w-24">{employee.name}</span>
                </td>
                {items.map((item) => {
                  const cell = cellIndex.get(cellKey(item.code, employee.employeeId));
                  const percent =
                    cell?.avgScore === null || cell?.avgScore === undefined ? null : cell.avgScore * 100;

                  return (
                    <td key={item.code} className="border-t p-1 text-center">
                      <button
                        type="button"
                        data-ev="nodes.heatmap.select_cell"
                        disabled={!cell || !onSelect}
                        onClick={() => onSelect?.({ item, employee, cell })}
                        title={
                          cell
                            ? `${employee.name} · ${item.label}: звонков ${formatCalls(cell.calls)}`
                            : 'Нет оценок'
                        }
                        className={`w-full rounded px-1 py-1 font-medium transition-shadow hover:shadow disabled:cursor-default disabled:hover:shadow-none ${resolveRampClass(percent)}`}
                      >
                        {formatScore(cell?.avgScore)}
                      </button>
                    </td>
                  );
                })}
                <td className="border-t p-1 text-center">
                  <span
                    className={`inline-block w-full rounded px-1 py-1 font-semibold ${resolveRampClass(
                      average === null || average === undefined ? null : average * 100
                    )}`}
                  >
                    {formatScore(average)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td className="sticky left-0 z-10 border-t bg-card p-2 align-middle font-medium">Средняя</td>
            {items.map((item) => {
              const average = itemAverages.get(item.code);

              return (
                <td key={item.code} className="border-t p-1 text-center">
                  <span
                    className={`inline-block w-full rounded px-1 py-1 font-semibold ${resolveRampClass(
                      average === null || average === undefined ? null : average * 100
                    )}`}
                  >
                    {formatScore(average)}
                  </span>
                </td>
              );
            })}
            <td className="border-t p-1 text-center">
              <span
                className={`inline-block w-full rounded px-1 py-1 font-semibold ${resolveRampClass(
                  overallAverage === null || overallAverage === undefined ? null : overallAverage * 100
                )}`}
              >
                {formatScore(overallAverage)}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default NodeHeatmapChart;
